import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormGroupDirective, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { AlbertValueObjectForm } from '@features/insured/models/member/member.model'
import { computed, effect, inject, Signal } from '@angular/core'
import { map, merge, Observable, scan, startWith, switchMap } from 'rxjs'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { CertificateCategory } from '@app/shared/models/certificate-category.model'

export type FieldGroup<T> = FormGroup<{
  value: FormControl<T | null>
  updatedByOrigami?: FormControl<boolean | null>
}>

export type OverrideFieldGroup<T> = FormGroup<{
  value: FormControl<T | null>
}>

export type OrigamiFieldGroup<T> = FormGroup<{
  value: FormControl<T | null>
  updatedByOrigami: FormControl<boolean | null>
}>

// Helper für einfaches Override Feld
export function createField<T>(initialValue: T | null = null): OverrideFieldGroup<T> {
  return new FormGroup({
    value: new FormControl<T | null>(initialValue),
  })
}

// Helper für Origami Feld (mit Flag)
export function createOrigamiField<T>(initialValue: T | null = null): OrigamiFieldGroup<T> {
  return new FormGroup({
    value: new FormControl<T | null>(initialValue),
    updatedByOrigami: new FormControl<boolean | null>(false), // Default false
  })
}

export function hasActualValue<T>(value: T | null | undefined): value is NonNullable<T> {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'string' && value.trim() === '') {
    return false
  }
  return !(typeof value === 'boolean' && !value)
}

export function buildAlbertValueObjectGroup<T>(
  fb: FormBuilder,
  initialValue: T | null = null,
  validators: ValidatorFn[] = [],
  includeChecked = false,
  updateOn: 'change' | 'blur' | 'submit' = 'blur'
): AlbertValueObjectForm<T> {
  const actualUpdateOn = (typeof initialValue === 'boolean') ? 'change' : updateOn
  const group = fb.group({
    localValue: [initialValue, { validators, updateOn: actualUpdateOn }],
    externalValue: [initialValue],
    changed: [false]
  }, { updateOn: actualUpdateOn }) as AlbertValueObjectForm<T>

  if (includeChecked) {
    group.addControl('checked', new FormControl<boolean>(initialValue !== null && initialValue !== undefined && initialValue !== '', { updateOn: 'change' }))
  }

  return group
}

export function isAlbertValueObjectForm(control: unknown): control is AlbertValueObjectForm<unknown> {
  if (control instanceof FormGroup) {
    const controls = control.controls as Record<string, unknown>
    return controls['localValue'] !== undefined && controls['externalValue'] !== undefined && controls['changed'] !== undefined
  }
  return false
}

function stripPath(node: unknown, segments: string[]): void {
  if (node === null || typeof node !== 'object' || segments.length === 0) return

  const [head, ...rest] = segments
  const container = node as Record<string, unknown>

  if (head === '**') {
    // Try matching the remaining segments at the current level, and also recurse into every child.
    if (rest.length > 0) stripPath(container, rest)
    for (const key of Object.keys(container)) {
      stripPath(container[key], segments)
    }
    return
  }

  if (rest.length === 0) {
    delete container[head!]
    return
  }

  stripPath(container[head!], rest)
}

/**
 * Returns a shallow clone of `obj` with the values at each dotted `path` replaced by `undefined`,
 * so that those paths do not contribute to a JSON-stringified comparison key. Missing paths are
 * ignored silently to keep the helper safe across heterogeneous form groups.
 *
 * A `**` segment in a path acts as a wildcard that matches any depth, so e.g.
 * `'**.additionalInfoContent.checked'` removes that field wherever it appears
 * in the tree.
 */
export function stripPaths(obj: Record<string, unknown>, paths: readonly string[]): Record<string, unknown> {
  if (paths.length === 0) return obj
  // Structured clone keeps nested objects independent from the live form value.
  const clone = structuredClone(obj)
  for (const path of paths) {
    stripPath(clone, path.split('.'))
  }
  return clone
}

/**
 * Narrows an AbstractControl to FormControl via instanceof check,
 * avoiding unsafe `as FormControl` casts while satisfying strict type checking.
 */
export function toFormControl(control: AbstractControl | null | undefined): FormControl | null {
  return control instanceof FormControl ? control : null
}

export interface FormControlBinding {
  /** Resolves the underlying FormControl for the bound group/control name. */
  control: Signal<FormControl | null>
  /** True when the bound group carries an `updatedByOrigami` flag set to true. */
  isUpdatedByOrigami: () => boolean
}

/**
 * Wires the shared form-control plumbing used by the `albert-*` input components
 * (textarea / checkbox / inputnumber): it resolves the target `FormControl` from the
 * parent `FormGroupDirective`, keeps its enabled/disabled state in sync with the
 * `disabled` signal, and exposes the `updatedByOrigami` flag.
 *
 * Must be called within an injection context (a component field initializer or
 * constructor) because it uses `inject` and `effect`.
 */
export function createFormControlBinding(
  groupName: Signal<string>,
  controlName: Signal<string>,
  disabled: Signal<boolean>
): FormControlBinding {
  const parentGroupDir = inject(FormGroupDirective)

  const control = computed(() => {
    const groupOrControl = parentGroupDir.form.get(groupName())
    if (groupOrControl instanceof FormControl) {
      return groupOrControl
    }
    return toFormControl(groupOrControl?.get(controlName()))
  })

  effect(() => {
    const ctrl = control()
    if (!ctrl) return

    if (disabled()) ctrl.disable({ emitEvent: false })
    else ctrl.enable({ emitEvent: false })
  })

  return {
    control,
    isUpdatedByOrigami: (): boolean =>
      parentGroupDir.form.get(groupName())?.get('updatedByOrigami')?.value === true
  }
}

export function categoryRequiredValidator(
  activeCategory: () => CertificateCategory | null,
  visibleCategories: readonly CertificateCategory[],
  isGateOpen?: (control: AbstractControl) => boolean
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const category = activeCategory()
    if (category !== null && !visibleCategories.includes(category)) {
      return null
    }
    if (isGateOpen !== undefined && !isGateOpen(control)) {
      return null
    }
    return Validators.required(control)
  }
}

export function requiredWithoutExternalValue(control: AbstractControl): ValidationErrors | null {
  const external: unknown = control.parent?.get('externalValue')?.value
  return hasActualValue(external) ? null : Validators.required(control)
}

/** Requires the localValue only while the sibling `checked` toggle of its value-object group is on. */
export function requiredWhenChecked(control: AbstractControl): ValidationErrors | null {
  return control.parent?.get('checked')?.value === true ? Validators.required(control) : null
}

export function normalizeOverrideValues<T>(value: T): T {
  const normalizeNode = (node: unknown): void => {
    if (node === null || typeof node !== 'object' || node instanceof Date) return

    if (Array.isArray(node)) {
      node.forEach(normalizeNode)
      return
    }

    const container = node as Record<string, unknown>
    const isOverrideGroup = 'localValue' in container && 'externalValue' in container
    if (isOverrideGroup && typeof container['localValue'] === 'string' && container['localValue'].trim() === '') {
      container['localValue'] = null
    }

    Object.values(container).forEach(normalizeNode)
  }

  const clone = structuredClone(value)
  normalizeNode(clone)
  return clone
}

export function revalidateControlTree(control: AbstractControl): void {
  if (control instanceof FormGroup) {
    Object.values(control.controls).forEach(child => revalidateControlTree(child))
  } else if (control instanceof FormArray) {
    control.controls.forEach(child => revalidateControlTree(child))
  }
  control.updateValueAndValidity({ onlySelf: true, emitEvent: false })
}

/**
 * Synchronizes a FormGroup's state based on the active category and a provided configuration.
 */
export function syncFormCategoryState(
  form: FormGroup,
  activeCategory: CertificateCategory | null,
  config: Record<string, CertificateCategory[] | undefined>
): void {
  if (activeCategory === null) return

  Object.entries(config).forEach(([controlName, allowedCategories]) => {
    const control = form.get(controlName)
    if (!control) return

    const isEnabled = allowedCategories?.includes(activeCategory) ?? false

    if (isEnabled && control.disabled) {
      control.enable()
    } else if (!isEnabled && control.enabled) {
      control.disable()
    }
  })
}

/** The control's current value, followed by its changes. */
export function valueWithChanges<T>(control: AbstractControl<T>): Observable<T> {
  return control.valueChanges.pipe(startWith(control.value))
}

/** Emits `state` right away and again whenever one of the controls changes, so derived form state is set up and kept current. */
export function onValueChanges<T>(state: T, ...controls: AbstractControl[]): Observable<T> {
  return merge(...controls.map(control => control.valueChanges)).pipe(startWith(null), map(() => state))
}

/**
 * Counts the value changes of the form input. Form values are no signals, so computeds read this to follow them.
 * Must be called in an injection context.
 */
export function formRevision(form: Signal<AbstractControl>): Signal<number> {
  return toSignal(
    toObservable(form).pipe(switchMap(control => control.valueChanges), scan(revision => revision + 1, 0)),
    { initialValue: 0 }
  )
}
